import { List, ListSubheader, Stack } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransferDailyElementsMutation } from '../../../../hooks/mutations/useActivityMutation';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { DailyMachine } from '../../../../services/apiSLTP/dailyReports/dailyMachines';
import { DailyRental } from '../../../../services/apiSLTP/dailyReports/dailyRentals';
import { DailySubContractor } from '../../../../services/apiSLTP/dailyReports/dailySubContractors';
import { DailySupply } from '../../../../services/apiSLTP/dailyReports/dailySupplies';
import { DailyTempWorker } from '../../../../services/apiSLTP/dailyReports/dailyTempWorkers';
import { DailyTool } from '../../../../services/apiSLTP/dailyReports/dailyTools';
import { DailyTruck } from '../../../../services/apiSLTP/dailyReports/dailyTrucks';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import ReportItemCard from '../../../domain/ReportItemCard';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ActivityResourceTransferModalProps extends ModalProps {
    refetch: () => void;
    activity: DataItem | null;
    activities: OptionType[];
    siteName?: string;
    dailyMachines?: DailyMachine[] | null;
    dailyTools?: DailyTool[] | null;
    dailyRentals?: DailyRental[] | null;
    dailyTempWorkers?: DailyTempWorker[] | null;
    dailySubContractors?: DailySubContractor[] | null;
    dailySupplies?: DailySupply[] | null;
    dailyTrucks?: DailyTruck[] | null;
}

const ActivityResourceTransferModal: React.FC<ActivityResourceTransferModalProps> = ({
    open,
    handleClose,
    refetch,
    activity,
    activities,
    siteName,
    dailyMachines,
    dailyTools,
    dailyRentals,
    dailyTempWorkers,
    dailySubContractors,
    dailySupplies,
    dailyTrucks,
}) => {
    const { t } = useTranslation();
    const [currentActivityId] = useState(activity?._id || '');

    const [selectedActivity, setSelectedActivity] = useState<OptionType | null>(null);

    const { mutate: transferDailyElements } = useTransferDailyElementsMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    return (
        <ModalBody
            title={t('activity')}
            subtitle={t('transferResources')}
            description={activity ? formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName) : ''}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                if (selectedActivity) transferDailyElements({ sourceActivityId: currentActivityId, targetActivityId: selectedActivity.value });
            }}
            disableSubmit={!selectedActivity?.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('toTheActivityOf')}
                required
            />
            <Stack>
                {dailyMachines && dailyMachines.length > 0 && (
                    <List
                        sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}
                        subheader={<ListSubheader>{capitalizeFirstLetter(t('machines'))}</ListSubheader>}
                    >
                        {dailyMachines.map((dm) => (
                            <ReportItemCard key={dm._id} title={dm.machineId.designation} hidePrice />
                        ))}
                    </List>
                )}
                {dailyTools && dailyTools.length > 0 && (
                    <List
                        sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}
                        subheader={<ListSubheader>{capitalizeFirstLetter(t('tools'))}</ListSubheader>}
                    >
                        {dailyTools.map((dt) => (
                            <ReportItemCard key={dt._id} title={dt.toolId.designation} hidePrice />
                        ))}
                    </List>
                )}
                {dailyRentals && dailyRentals.length > 0 && (
                    <List
                        sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}
                        subheader={<ListSubheader>{capitalizeFirstLetter(t('rentals'))}</ListSubheader>}
                    >
                        {dailyRentals.map((dr) => (
                            <ReportItemCard key={dr._id} title={`${dr.designation} - ${dr.rentalTypeId.designation}`} hidePrice />
                        ))}
                    </List>
                )}
                {dailyTempWorkers && dailyTempWorkers.length > 0 && (
                    <List
                        sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}
                        subheader={<ListSubheader>{capitalizeFirstLetter(t('tempWorkers'))}</ListSubheader>}
                    >
                        {dailyTempWorkers.map((dtw) => (
                            <ReportItemCard key={dtw._id} title={`${dtw.tempWorkerName} - ${dtw.tempWorkerTypeId.designation}`} hidePrice />
                        ))}
                    </List>
                )}
                {/*dailySubContractors && dailySubContractors.length > 0 && (
                    <List
                        sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}
                        subheader={<ListSubheader>{capitalizeFirstLetter(t('subContractors'))}</ListSubheader>}
                    >
                        {dailySubContractors.map((dsc) => (
                            <ReportItemCard key={dsc._id} title={dsc.subContractorTypeId.designation} hidePrice />
                        ))}
                    </List>
                )*/}
                {dailySupplies && dailySupplies.length > 0 && (
                    <List
                        sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}
                        subheader={<ListSubheader>{capitalizeFirstLetter(t('supplies'))}</ListSubheader>}
                    >
                        {dailySupplies.map((ds) => (
                            <ReportItemCard key={ds._id} title={ds.companyId.designation} hidePrice />
                        ))}
                    </List>
                )}
                {dailyTrucks && dailyTrucks.length > 0 && (
                    <List
                        sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}
                        subheader={<ListSubheader>{capitalizeFirstLetter(t('trucks'))}</ListSubheader>}
                    >
                        {dailyTrucks.map((dt) => (
                            <ReportItemCard key={dt._id} title={dt.truckId.designation} hidePrice />
                        ))}
                    </List>
                )}
            </Stack>
        </ModalBody>
    );
};

export default ActivityResourceTransferModal;
